import { useState } from "react";
import { updateVideoStream } from "@/entities/player";
import {
  Gondola,
  GondolaSkuSectionsResponse,
  SkuSection,
  useGondolasSkuSectionsQuery,
  WithIndex,
} from "@/shared/api";
import { useAppDispatch, useAppSelector } from "@/app/appStore";

export function getCameraSkuSections(
  data: GondolaSkuSectionsResponse,
  cameraId: string | null
): WithIndex<SkuSection>[] {
  return (
    data?.results
      .map((cameras, index) => ({
        ...cameras[cameraId || ""],
        index,
      }))
      .filter((item) => data.results[item.index][cameraId || ""]) || []
  );
}

export const findCamerasWithSkuSection = (
  skuSectionId: number,
  response: GondolaSkuSectionsResponse
): any => {
  const result: any = [];

  response.results.forEach((cameraObject) => {
    Object.entries(cameraObject).forEach(([cameraId, skuSection]) => {
      if (skuSection.sku_section_id === skuSectionId) {
        result.push({ cameraId, skuSection });
      }
    });
  });

  return result;
};

export function findSkuSectionById(
  data: GondolaSkuSectionsResponse,
  cameraId: string,
  skuSectionId: number
): WithIndex<SkuSection> | null {
  const cameraSkuSections = getCameraSkuSections(data, cameraId);
  return (
    cameraSkuSections.find((item) => item.sku_section_id === skuSectionId) ||
    null
  );
}

export const useCameraData = (selectedGondola: Gondola | null) => {
  const currentCamera = useAppSelector(
    (state) => state.player.videoStream.cameraId
  );
  const dispatch = useAppDispatch();

  const [selectedSkuSectionId, setSelectedSkuSectionId] = useState<
    number | null
  >(null);
  const [selectedPolygon, setSelectedPolygon] =
    useState<WithIndex<SkuSection> | null>(null);

  const { data, isFetching, isLoading } = useGondolasSkuSectionsQuery(
    {
      gondolaId: String(selectedGondola?.id),
    },
    { skip: !selectedGondola }
  );

  const onSelectCamera = (cameraId: string) => {
    if (selectedSkuSectionId && data) {
      const updatedPolygon = findSkuSectionById(
        data,
        cameraId,
        selectedSkuSectionId
      );

      setSelectedPolygon(updatedPolygon);
    }

    dispatch(updateVideoStream({ cameraId }));
  };

  const onSelectPolygon = (polygon: WithIndex<SkuSection>) => {
    setSelectedSkuSectionId(polygon.sku_section_id);
    setSelectedPolygon(polygon);
  };

  const onSelectSkuSectionId = (skuSectionId: number | null) => {
    setSelectedSkuSectionId(skuSectionId);
  };

  const cameraSkuSections = data?.results
    ? getCameraSkuSections(data, currentCamera)
    : null;

  const compressedImage = cameraSkuSections?.[0]?.compressed_image || null;

  const imageSize = {
    width: cameraSkuSections?.[0]?.width || 1920,
    height: cameraSkuSections?.[0]?.height || 1080,
  };

  const relatedCameraIds = selectedGondola?.shelfTrackingCameras || [];

  const camerasWithSelectedSkuSection =
    selectedPolygon && data
      ? findCamerasWithSkuSection(selectedPolygon.sku_section_id, data)
      : null;

  return {
    isLoading: isLoading || isFetching,
    relatedCameraIds,
    currentCamera,
    cameraSkuSections,
    onSelectCamera,
    image: compressedImage,
    gondolaSkuSections: data?.results,
    imageSize,
    selectedPolygon,
    onSelectPolygon,
    selectedSkuSectionId,
    onSelectSkuSectionId,
    camerasWithSelectedSkuSection,
  };
};
